import React from 'react';
import { FiStar, FiAward, FiHeart, FiTrendingUp } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHelmet from '../components/SEOHelmet';
import { metaTags } from '../components/seoConfig';
import { Images } from '../utils/cloudflareImages';
import { Blob } from '../components/Blob';
import Gigi from '../img/gigifromgigihairbraiding.jpg';

interface Stat {
  number: string;
  label: string;
  icon: React.ReactNode;
}

export const About: React.FC = () => {
  const stats: Stat[] = [
    {
      number: '10+',
      label: 'Years Experience',
      icon: <FiAward className="w-8 h-8" />,
    },
    {
      number: '1000+',
      label: 'Happy Clients',
      icon: <FiHeart className="w-8 h-8" />,
    },
    {
      number: '5★',
      label: 'Rating',
      icon: <FiStar className="w-8 h-8" />,
    },
    {
      number: '∞',
      label: 'Styles',
      icon: <FiTrendingUp className="w-8 h-8" />,
    },
  ];

  return (
    <>
      <SEOHelmet
        title={metaTags.about.title}
        description={metaTags.about.description}
        keywords={metaTags.about.keywords}
      />

      {/* Hero Section */}
      <motion.section
        className="pt-32 pb-20 px-6 md:px-20 bg-[#FAF8F4]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm tracking-widest uppercase text-[#6B6970] mb-4">
            Our Story
          </p>
          <h1 className="font-serif text-6xl md:text-7xl text-[#0B0B0B] mb-6 leading-tight">
            About GiGi
          </h1>
          <p className="text-xl text-[#6B6970] max-w-2xl mx-auto leading-relaxed">
            Your go-to braiding expert in Irvington, NJ
          </p>
        </div>
      </motion.section>

      {/* Meet GiGi Section */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-white relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <Blob
          variant={3}
          className="-z-10 right-0 top-32 w-[450px] h-[450px]"
          style={{ opacity: 0.05 }}
        />
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Side */}
            <div className="relative">
              <div className="relative">
                <img
                  src={Gigi}
                  alt="GiGi - Your Braiding Expert"
                  className="w-full h-[600px] object-cover rounded-2xl shadow-lg"
                  loading="eager"
                />
                <div className="absolute inset-0 border-4 border-[#C97B63] -translate-x-6 -translate-y-6 -z-10 rounded-2xl"></div>
              </div>
            </div>

            {/* Text Side */}
            <div className="space-y-6">
              <div className="inline-block bg-[#FAF8F4] px-4 py-2 rounded-md">
                <p className="text-sm tracking-widest uppercase text-[#6B6970]">
                  Your Braiding Expert
                </p>
              </div>

              <h2 className="font-serif text-5xl md:text-6xl text-[#0B0B0B] leading-tight">
                Meet GiGi
              </h2>

              <div className="space-y-4 text-lg text-[#0B0B0B]/80 leading-relaxed">
                <p>
                  Hey! I'm GiGi, and I've been doing hair for over 20 years.
                  <strong> GiGi Hair Braiding</strong> isn't just a job for
                  me—it's what I love to do. Making people look and feel amazing
                  is what gets me up every morning!
                </p>

                <p>
                  I've done thousands of heads over the years, and each one
                  teaches me something new. Whether you're new to braids or
                  you've been getting them your whole life, I'm here to make
                  sure you leave looking fire.
                </p>

                <p>
                  When you come to my salon, it's not just about getting your
                  hair done. It's about feeling comfortable, having good
                  conversation, and walking out with a smile. That's what
                  matters to me.
                </p>
              </div>

              <div className="pt-6">
                <Link
                  to="/contact"
                  className="inline-block bg-[#0B0B0B] text-white px-10 py-4 rounded-2xl font-medium hover:shadow-lg transition-all"
                >
                  BOOK WITH ME
                </Link>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Stats Section */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-[#FAF8F4] relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <Blob
          variant={1}
          className="-z-10 left-10 top-20 w-80 h-80"
          style={{ opacity: 0.05 }}
        />
        <Blob
          variant={4}
          className="-z-10 right-0 bottom-20 w-96 h-96"
          style={{ opacity: 0.04 }}
        />
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 },
              },
            }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-white rounded-2xl shadow-sm">
                    <div className="text-[#C97B63]">{stat.icon}</div>
                  </div>
                </div>
                <p className="font-serif text-5xl md:text-6xl text-[#0B0B0B] mb-2">
                  {stat.number}
                </p>
                <p className="text-sm tracking-widest uppercase text-[#6B6970]">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Mission Section */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-white relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <Blob
          variant={2}
          className="-z-10 left-0 top-40 w-[400px] h-[400px]"
          style={{ opacity: 0.04 }}
        />
        <Blob
          variant={3}
          className="-z-10 right-10 bottom-32 w-72 h-72"
          style={{ opacity: 0.05 }}
        />
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm tracking-widest uppercase text-[#6B6970] mb-4">
              What We're About
            </p>
            <h2 className="font-serif text-5xl md:text-6xl text-[#0B0B0B] mb-6 leading-tight">
              What Matters To Us
            </h2>
            <p className="text-xl text-[#6B6970] max-w-2xl mx-auto leading-relaxed">
              Quality braids, happy clients, healthy hair
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Mission Item 1 */}
            <div className="bg-white p-10 shadow-sm rounded-2xl border-t-4 border-[#C97B63]">
              <div className="w-full h-48 overflow-hidden mb-6">
                <img
                  src={Images.hairMaintenance}
                  alt="Personal Care"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>

              <h3 className="font-serif text-2xl text-[#0B0B0B] mb-3">
                We Care About You
              </h3>
              <p className="text-[#6B6970] leading-relaxed">
                Every person who sits in my chair gets my full attention. We'll
                talk about what you want, and I'll make sure it's done right.
              </p>
            </div>

            {/* Mission Item 2 */}
            <div className="bg-white p-10 shadow-sm rounded-2xl border-t-4 border-[#C97B63]">
              <div className="w-full h-48 overflow-hidden mb-6">
                <img
                  src={Images.artisticBraids}
                  alt="Quality Work"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold text-salon-forest mb-3">
                Quality Over Everything
              </h3>
              <p className="text-salon-forest/70 leading-relaxed">
                I take my time and do it right. No rushing, no cutting corners.
                Your braids should look good and last long.
              </p>
            </div>

            {/* Mission Item 3 */}
            <div className="bg-white p-10 shadow-sm rounded-2xl border-t-4 border-[#C97B63]">
              <div className="w-full h-48 overflow-hidden mb-6">
                <img
                  src={Images.boxBraids}
                  alt="Community"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold text-salon-forest mb-3">
                Part of the Community
              </h3>
              <p className="text-salon-forest/70 leading-relaxed">
                I'm from here, and I care about this community. When you come
                through, you're not just a client—you're family.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Services Preview */}
      <motion.section
        className="py-24 px-4 sm:px-6 lg:px-8 bg-white"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-salon-olive font-semibold tracking-widest uppercase text-sm mb-4">
              What I Do
            </p>
            <h2 className="text-4xl sm:text-5xl font-black text-salon-forest mb-6">
              Services I Offer
            </h2>
            <p className="text-xl text-salon-forest/70 max-w-2xl mx-auto">
              From classic styles to something totally unique—if you can dream
              it, I can braid it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-10 shadow-sm border-l-4 border-[#C97B63] rounded-2xl">
              <h3 className="font-serif text-2xl text-[#0B0B0B] mb-6">
                Braiding Styles
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-[#0B0B0B]/80">
                  <span className="text-[#C97B63] font-medium text-xl mt-1">
                    ✓
                  </span>
                  <span>Box Braids (small, medium, or large)</span>
                </li>
                <li className="flex items-start gap-3 text-[#0B0B0B]/80">
                  <span className="text-[#C97B63] font-medium text-xl mt-1">
                    ✓
                  </span>
                  <span>Cornrows in any pattern you want</span>
                </li>
                <li className="flex items-start gap-3 text-[#0B0B0B]/80">
                  <span className="text-[#C97B63] font-medium text-xl mt-1">
                    ✓
                  </span>
                  <span>Two-Strand Twists</span>
                </li>
                <li className="flex items-start gap-3 text-[#0B0B0B]/80">
                  <span className="text-[#C97B63] font-medium text-xl mt-1">
                    ✓
                  </span>
                  <span>Goddess Braids with beads or accessories</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-10 shadow-sm border-l-4 border-[#C97B63] rounded-2xl">
              <h3 className="font-serif text-2xl text-[#0B0B0B] mb-6">
                Hair Care
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-salon-forest/80">
                  <span className="text-salon-olive font-bold text-xl mt-1">
                    ✓
                  </span>
                  <span>Talk to me about what's best for your hair</span>
                </li>
                <li className="flex items-start gap-3 text-salon-forest/80">
                  <span className="text-salon-olive font-bold text-xl mt-1">
                    ✓
                  </span>
                  <span>Deep conditioning treatments</span>
                </li>
                <li className="flex items-start gap-3 text-salon-forest/80">
                  <span className="text-salon-olive font-bold text-xl mt-1">
                    ✓
                  </span>
                  <span>Braid touchups and tightening</span>
                </li>
                <li className="flex items-start gap-3 text-salon-forest/80">
                  <span className="text-salon-olive font-bold text-xl mt-1">
                    ✓
                  </span>
                  <span>Tips on keeping your hair healthy</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-block bg-[#0B0B0B] text-white px-10 py-4 rounded-2xl font-medium hover:shadow-lg transition-all"
            >
              SEE ALL SERVICES
            </Link>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        className="py-32 px-6 md:px-20 bg-[#0B0B0B]"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-6 leading-tight">
            Let's Get You Booked!
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Come through and let's make your hair look amazing. You won't regret
            it!
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-[#0B0B0B] px-10 py-4 rounded-2xl font-medium hover:shadow-lg transition-all"
          >
            CONTACT ME TODAY
          </Link>
        </div>
      </motion.section>
    </>
  );
};

export default About;
